import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfirmationDialogComponent } from '@app/components';
import { JsonApiError } from '@app/errors/json-api.error';
import { Node } from '@app/models/documents/directory/node.model';
import { NodeInTreeFormat } from '@app/modules/documents-v2/interfaces/node-in-tree-format.interface';
import { DocumentFoldersService } from '@app/modules/documents-v2/services/document-folders.service';
import { NotifyService } from '@app/services';
import { DraggableData } from '@app/types';
import * as Sentry from '@sentry/browser';

@Injectable({
    providedIn: 'root',
})
export class DocumentMoveNodeService {
    constructor(
        private notify: NotifyService,
        private documentFoldersService: DocumentFoldersService
    ) {}

    public getNodesToValidate(nodes: Node[]): [number[], number[]] {
        // group nodes so we only validate what's needed
        const publicViewOnlyIds: number[] = [],
            templateNodeIds: number[] = [];
        nodes.forEach((node: Node) => {
            if (node.isCompanyDocument() && node.source.isViewOnly()) {
                publicViewOnlyIds.push(node.id);
            } else {
                templateNodeIds.push(node.id);
            }
        });

        return [publicViewOnlyIds, templateNodeIds];
    }

    /**
     * This method retrieves the nodes and nodeIds associated to the onDrop methods for the sidebar and index view.
     */
    public async extractNodes(
        data: Node | Node[] | NodeInTreeFormat | DraggableData | DraggableData[]
    ): Promise<{ nodes: Node[]; nodeIds: number[] }> {
        let nodeIds = [];
        let nodes: Node[] = [];
        if (data instanceof Node) {
            nodeIds.push(data.id);
            nodes.push(data);
        }
        if (Array.isArray(data)) {
            nodeIds = data.map((node) => (node as Node).id);
            nodes = data as Node[];
        }
        if ('nodeId' in data) {
            nodeIds.push(data.nodeId);
            nodes.push(await Node.with(['source']).find(data.nodeId));
        }

        return { nodes: nodes, nodeIds: nodeIds };
    }

    public parseError(err: HttpErrorResponse): {
        moveConflicts: string[];
        header: string;
        description: string;
    } {
        const parsedError = {
            moveConflicts: [],
            header: 'document.sameNameHeader',
            description: 'document.sameNameError',
        };
        switch (err.status) {
            case 409:
                parsedError.moveConflicts = err.error?.errors?.map((e: { detail?: string }) => e?.detail);
                parsedError.header = 'document.sameNameHeader';
                parsedError.description = 'document.sameNameError';
                break;
            default:
                /**
                 * Extract any validation errors to be bubbled up to the frontend.
                 */
                parsedError.moveConflicts = err.error?.errors
                    ?.filter((e: JsonApiError) => e.source?.pointer === 'data.attach.node_id')
                    .map((e: JsonApiError) => e.detail);
                if (parsedError.moveConflicts) {
                    parsedError.header = 'document.cannotMoveItems';
                    parsedError.description = 'document.moveFailed';
                    Sentry.captureException({ name: 'Move node error', message: err });
                    this.notify.error('document.cannotMoveItems');
                }
                break;
        }
        return parsedError;
    }

    public async validateMove(
        nodes: Node[],
        targetId: number,
        privateViewOnlyDocForbiddenDialog: ConfirmationDialogComponent,
        publicDataForbiddenDialog: ConfirmationDialogComponent
    ): Promise<boolean> {
        const [publicViewOnlyIds, templateNodeIds] = this.getNodesToValidate(nodes);

        const isMakingTemplatesPublic = await this.documentFoldersService.isMoveMakingNodesPublic(
            templateNodeIds,
            targetId
        );

        if (isMakingTemplatesPublic) {
            // Moving templates and folders from template library to public is forbidden
            await publicDataForbiddenDialog.open();
            return false;
        }

        const isMakingViewOnlyPrivate = await this.documentFoldersService.isMoveMakingNodesPrivate(
            publicViewOnlyIds,
            targetId
        );

        if (isMakingViewOnlyPrivate) {
            privateViewOnlyDocForbiddenDialog.open();
            return false;
        }

        return true;
    }
}
